import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, Loader2, Lock, Minus, Plus, Trash2 } from 'lucide-react'
import StoreHeader from '@/components/store/StoreHeader'
import StoreFooter from '@/components/store/StoreFooter'
import { api, type CustomerProfile, type InventoryItemNameRow } from '@/lib/api'
import { useInventoryCart } from '@/hooks/useInventoryCart'
import { storeForm } from '@/components/store/storeFormStyles'
import { SALES_TAX_LABEL, salesTaxFor } from '@/lib/salesTax'

function unitPrice(row: InventoryItemNameRow | undefined): number {
  if (!row) return 0
  const p = row.discount_percentage
  if (p != null && p >= 1 && p <= 50 && row.discounted_price != null) {
    return row.discounted_price
  }
  return row.retail_price ?? 0
}

function maxStockCap(inStock: number | null | undefined): number | undefined {
  if (inStock == null) return undefined
  const n = Math.floor(Number(inStock))
  return n > 0 ? n : undefined
}

/** Read-only account fields (email, phone): greyed so they don't look editable. */
const lockedInput = `${storeForm.input.replace('bg-white', 'bg-stone-100')} cursor-not-allowed border-stone-200`

/** Line-total column width, shared by cart lines and the total row so amounts line up. */
const AMOUNT_COL = 'w-14 shrink-0 text-right sm:w-24'
/** Same width as the remove button, so the total row's amount sits under the line amounts. */
const REMOVE_COL = 'w-7 shrink-0 sm:w-9'

type CheckoutQtyPillProps = {
  qty: number
  atMax: boolean
  inventoryId: string
  inStock: number | null | undefined
  removeOne: (id: string) => void
  addOne: (id: string, inStock?: number | null) => void
}

/** Compact stepper matching the item detail sheet. */
function CheckoutQtyPill({ qty, atMax, inventoryId, inStock, removeOne, addOne }: CheckoutQtyPillProps) {
  return (
    <div
      className="flex h-8 shrink-0 items-center rounded-full border border-stone-300 bg-white sm:h-10"
      role="group"
      aria-label="Quantity in cart"
    >
      <button
        type="button"
        onClick={() => removeOne(inventoryId)}
        className="flex h-8 w-7 items-center justify-center rounded-full text-stone-800 hover:bg-stone-100 sm:h-10 sm:w-9"
        aria-label="Decrease quantity"
      >
        <Minus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
      </button>
      <span className="w-6 select-none text-center text-xs font-semibold tabular-nums text-stone-900 sm:w-7 sm:text-sm">
        {qty}
      </span>
      <button
        type="button"
        disabled={atMax}
        onClick={() => addOne(inventoryId, inStock)}
        className="flex h-8 w-7 items-center justify-center rounded-full text-stone-800 hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-30 sm:h-10 sm:w-9"
        aria-label="Increase quantity"
        title={atMax ? 'Maximum available in cart' : 'Add one'}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  )
}

export default function CheckoutPage() {
  const [inventoryItems, setInventoryItems] = useState<InventoryItemNameRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { lines, setQty, addOne, removeOne, clearAll, totalQty } = useInventoryCart()
  const navigate = useNavigate()
  // Signed-in customer: `undefined` while loading, `null` when signed out (or the token is rejected).
  const [profile, setProfile] = useState<CustomerProfile | null | undefined>(undefined)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!localStorage.getItem('srisai_access_token')) {
      setProfile(null)
      return
    }
    let cancelled = false
    api.auth
      .me()
      .then((res) => {
        if (cancelled) return
        const me = res.success ? res.data : null
        setProfile(me ?? null)
        if (me) {
          setFirstName(me.first_name ?? '')
          setLastName(me.last_name ?? '')
        }
      })
      .catch(() => {
        if (!cancelled) setProfile(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.storefront
      .getInventoryItemNames()
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) setInventoryItems(res.data.items)
        else setInventoryItems([])
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message)
          setInventoryItems([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const byId = useMemo(
    () => new Map(inventoryItems.map((r) => [r.inventory_id, r])),
    [inventoryItems]
  )

  const entries = useMemo(
    () =>
      Object.entries(lines)
        .filter(([, q]) => q > 0)
        .map(([inventoryId, qty]) => ({
          inventoryId,
          qty,
          row: byId.get(inventoryId),
        })),
    [lines, byId]
  )

  const subtotal = useMemo(
    () => entries.reduce((sum, e) => sum + unitPrice(e.row) * e.qty, 0),
    [entries]
  )

  const salesTax = salesTaxFor(subtotal)
  const total = subtotal + salesTax

  // Email (sign-in ID) and phone come from the account and are read-only here.
  const email = profile?.email ?? ''
  const phone = profile?.phone?.trim() ?? ''
  const detailsOk = firstName.trim().length > 0 && lastName.trim().length > 0
  // Checkout needs a signed-in customer whose account has a phone number.
  const canCheckout = !!profile && phone.length > 0 && detailsOk && entries.length > 0 && !submitting

  const handleCheckout = async () => {
    if (!canCheckout || !profile) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const changes = {
        ...(firstName.trim() !== profile.first_name ? { first_name: firstName.trim() } : {}),
        ...(lastName.trim() !== profile.last_name ? { last_name: lastName.trim() } : {}),
      }
      if (Object.keys(changes).length > 0) {
        const res = await api.auth.updateMe(changes)
        if (res.success && res.data) setProfile(res.data)
      }
      // The server prices the order from inventory, adds tax and assigns the order number.
      const placed = await api.orders.place({
        lines: entries.map(({ inventoryId, qty }) => ({ inventory_id: inventoryId, quantity: qty })),
        notes: notes.trim() || undefined,
      })
      const order = placed.data
      if (!placed.success || !order) throw new Error('Could not place your order. Please try again.')
      clearAll()
      // Swap checkout for home in history, then open the confirmation: browser back lands on home.
      navigate('/', { replace: true })
      navigate(`/order/${order.order_number}`, { state: { order } })
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Could not complete checkout. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const checkoutHint =
    profile === undefined
      ? null
      : !profile
        ? 'Sign in to complete checkout.'
        : !phone
          ? 'Your account has no phone number. Ask the store to add one to your account to check out.'
          : !detailsOk
            ? 'Enter your first name and last name.'
            : null

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-orange-50 via-stone-100 to-stone-300/60">
      <StoreHeader inventoryItems={inventoryItems} inventoryLoading={loading} />

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pb-6 pt-3 sm:px-6 sm:pt-4 lg:px-8">
        <Link
          to="/"
          className="mb-2 inline-flex items-center gap-0.5 text-xs font-medium text-stone-600 hover:text-primary-700 sm:text-sm"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Back to shopping
        </Link>
        <h1 className="text-base font-semibold text-stone-900 sm:text-xl">Checkout</h1>
        <p className="mt-0.5 text-xs text-stone-500 sm:text-sm">
          Review your cart and submit when you are ready. We will follow up to confirm your order.
        </p>

        {error ? (
          <p className="mt-4 text-xs text-red-600 sm:text-sm" role="alert">
            {error}
          </p>
        ) : null}

        {totalQty === 0 ? (
          <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
            <p className="text-sm text-stone-700">Your cart is empty.</p>
            <Link to="/" className="mt-2 inline-block text-xs font-semibold text-primary-700 hover:underline sm:text-sm">
              Browse items
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
              <ul className="divide-y divide-stone-100">
                {entries.map(({ inventoryId, qty, row }) => {
                  const price = unitPrice(row)
                  const max = maxStockCap(row?.in_stock)
                  return (
                    <li key={inventoryId} className="flex items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-xs font-medium leading-snug text-stone-900 sm:text-sm">
                          {row?.item_name ?? 'Item no longer listed'}
                        </p>
                        <p className="mt-0.5 text-[11px] tabular-nums text-stone-500 sm:text-xs">
                          ${price.toFixed(2)} each
                          {row?.item_num ? <span className="hidden font-mono sm:inline"> · #{row.item_num}</span> : null}
                        </p>
                      </div>
                      <CheckoutQtyPill
                        qty={qty}
                        atMax={max !== undefined && qty >= max}
                        inventoryId={inventoryId}
                        inStock={row?.in_stock}
                        removeOne={removeOne}
                        addOne={addOne}
                      />
                      <p className={`${AMOUNT_COL} text-xs font-semibold tabular-nums text-stone-900 sm:text-sm`}>
                        ${(price * qty).toFixed(2)}
                      </p>
                      <button
                        type="button"
                        onClick={() => setQty(inventoryId, 0, null)}
                        className={`${REMOVE_COL} flex h-7 items-center justify-center rounded-full text-stone-400 hover:bg-red-50 hover:text-red-600 sm:h-9`}
                        aria-label="Remove from cart"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  )
                })}
              </ul>
              <div className="space-y-1 border-t border-stone-100 bg-stone-50/80 px-3 py-2.5 sm:px-4 sm:py-3">
                <div className="flex items-center gap-2 text-xs text-stone-600 sm:gap-3 sm:text-sm">
                  <span className="min-w-0 flex-1">
                    Subtotal · {totalQty} {totalQty === 1 ? 'item' : 'items'}
                  </span>
                  <span className={`${AMOUNT_COL} tabular-nums`}>${subtotal.toFixed(2)}</span>
                  <span className={REMOVE_COL} aria-hidden />
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-600 sm:gap-3 sm:text-sm">
                  <span className="min-w-0 flex-1">Sales tax ({SALES_TAX_LABEL})</span>
                  <span className={`${AMOUNT_COL} tabular-nums`}>${salesTax.toFixed(2)}</span>
                  <span className={REMOVE_COL} aria-hidden />
                </div>
                <div className="flex items-center gap-2 border-t border-stone-200 pt-1.5 sm:gap-3">
                  <span className="min-w-0 flex-1 text-xs font-semibold text-stone-800 sm:text-sm">
                    Total <span className="font-normal text-stone-500">(incl. tax)</span>
                  </span>
                  <span className={`${AMOUNT_COL} text-sm font-bold tabular-nums text-stone-900 sm:text-base`}>
                    ${total.toFixed(2)}
                  </span>
                  <span className={REMOVE_COL} aria-hidden />
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:mt-6 sm:p-5">
              <div>
                <h2 className={storeForm.sectionLabel}>Your details</h2>
                <p className={`mt-0.5 ${storeForm.hint}`}>
                  {profile
                    ? 'From your account. Name changes are saved when you check out.'
                    : 'Sign in so we can confirm your order with you.'}
                </p>
              </div>

              {profile === undefined ? (
                <div className="flex justify-center py-3 text-stone-400" aria-label="Loading your details">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : !profile ? (
                <Link to="/login?next=/checkout" className={storeForm.submit}>
                  Sign in to check out
                </Link>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block space-y-1">
                      <span className={storeForm.fieldLabel}>First name</span>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={`${storeForm.input} ${firstName.trim() ? storeForm.inputOk : storeForm.inputBad}`}
                        autoComplete="given-name"
                      />
                    </label>
                    <label className="block space-y-1">
                      <span className={storeForm.fieldLabel}>Last name</span>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={`${storeForm.input} ${lastName.trim() ? storeForm.inputOk : storeForm.inputBad}`}
                        autoComplete="family-name"
                      />
                    </label>
                  </div>
                  <div className="space-y-1">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="block space-y-1">
                        <span className={`flex items-center gap-1 ${storeForm.fieldLabel}`}>
                          Email <Lock className="h-3 w-3" aria-hidden />
                        </span>
                        <input
                          type="email"
                          value={email}
                          readOnly
                          aria-readonly="true"
                          className={`${lockedInput} text-stone-600`}
                        />
                      </label>
                      <label className="block space-y-1">
                        <span className={`flex items-center gap-1 ${storeForm.fieldLabel}`}>
                          Phone <Lock className="h-3 w-3" aria-hidden />
                        </span>
                        <input
                          type="tel"
                          value={phone || 'Not on your account'}
                          readOnly
                          aria-readonly="true"
                          className={`${lockedInput} ${phone ? 'text-stone-600' : 'italic text-stone-400'}`}
                        />
                      </label>
                    </div>
                    <p className={storeForm.hint}>Email and phone are tied to your sign-in and can&apos;t be changed here.</p>
                  </div>
                </>
              )}

              <label className="block space-y-1">
                <span className={storeForm.fieldLabel}>Notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className={`resize-y ${storeForm.input} ${storeForm.inputOk}`}
                  placeholder="Pickup time, substitutions, etc."
                />
              </label>
            </div>

            {submitError ? (
              <p className={`mt-4 ${storeForm.error}`} role="alert">
                {submitError}
              </p>
            ) : null}

            <div className="mt-4 flex flex-col items-stretch gap-2 sm:mt-6 sm:items-end">
              <button
                type="button"
                onClick={handleCheckout}
                disabled={!canCheckout}
                className={storeForm.submit}
              >
                {submitting ? 'Submitting…' : `Complete checkout · $${total.toFixed(2)}`}
              </button>
              {checkoutHint ? <p className={`text-center sm:text-right ${storeForm.hint}`}>{checkoutHint}</p> : null}
            </div>
          </>
        )}

        <StoreFooter detailsClassName="mt-6 sm:mt-12" />
      </main>
    </div>
  )
}
